'use client';

import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { Lock, UserCheck, UserPlus } from 'lucide-react';

import { useT } from '@/hooks';

type SlotState = 'filled' | 'available' | 'locked';

interface SlotGridProps {
  used: number;
  available: number;
  max: number;
}

/**
 * SlotGrid Component
 * Visualizes referral slots with different states using MUI components.
 */
export const SlotGrid: React.FC<SlotGridProps> = ({ used, available, max }) => {
  const { t } = useT();

  const slots = Array.from({ length: max }, (_, i) => {
    const index = i + 1;
    if (index <= used) return 'filled' as SlotState;
    if (index <= available) return 'available' as SlotState;
    return 'locked' as SlotState;
  });

  const renderSlot = (state: SlotState, i: number) => (
    <SlotCard key={i} index={i + 1} state={state} t={t} />
  );

  const renderedSlotsList = slots.map(renderSlot);

  return (
    <Box
      sx={{
        display: { xs: 'flex', md: 'grid' },
        overflowX: { xs: 'auto', md: 'visible' },
        gridTemplateColumns: {
          md: 'repeat(10, 1fr)',
        },
        gap: 1.5,
        mt: 3,
        pb: { xs: 1, md: 0 },
        '&::-webkit-scrollbar': { display: 'none' },
        '-ms-overflow-style': 'none',
        'scrollbar-width': 'none',
      }}
    >
      {renderedSlotsList}
    </Box>
  );
};

interface SlotCardProps {
  index: number;
  state: SlotState;
  t: any;
}

const SlotCard: React.FC<SlotCardProps> = ({ index, state, t }) => {
  const isFilled = state === 'filled';
  const isAvailable = state === 'available';
  const isLocked = state === 'locked';

  const cardContent = (
    <Box
      sx={{
        position: 'relative',
        width: { xs: 68, sm: 76, md: '100%' },
        minWidth: { xs: 68, sm: 76, md: 'auto' },
        aspectRatio: '1/1',
        borderRadius: 3,
        border: '1px solid',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: isAvailable ? 'pointer' : 'default',

        // Dynamic colors based on state
        bgcolor: isFilled
          ? 'primary.lighter'
          : isAvailable
            ? 'success.lighter'
            : 'action.disabledBackground',
        borderColor: isFilled ? 'primary.light' : isAvailable ? 'success.light' : 'divider',
        color: isFilled ? 'primary.main' : isAvailable ? 'success.main' : 'text.disabled',
        borderStyle: isAvailable ? 'dashed' : 'solid',
        opacity: isLocked ? 0.6 : 1,

        '&:hover': {
          transform: !isLocked ? 'translateY(-2px)' : 'none',
          bgcolor: isAvailable ? 'success.light' : undefined,
          '& .slot-overlay': { opacity: 1 },
        },
      }}
    >
      {isFilled && <UserCheck size={20} style={{ marginBottom: 4 }} />}
      {isAvailable && (
        <UserPlus
          size={20}
          className="group-hover:scale-110"
          style={{ marginBottom: 4, transition: 'transform 0.2s' }}
        />
      )}
      {isLocked && <Lock size={16} style={{ marginBottom: 4 }} />}

      <Typography
        variant="caption"
        sx={{
          fontSize: '0.625rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.025em',
        }}
      >
        {isLocked ? t('referral.locked') : t('referral.slotX', { index })}
      </Typography>

      {isLocked && (
        <Box
          className="slot-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(20, 25, 35, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.2s',
            zIndex: 1,
            p: 1,
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontSize: '0.55rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.1 }}
          >
            {t('referral.unlockAt', { volume: index * 200 - 600 })}
          </Typography>
        </Box>
      )}
    </Box>
  );

  if (isLocked) {
    return (
      <Tooltip title={t('referral.requiresTradingVolume', { volume: index * 200 - 600 })} arrow>
        {cardContent}
      </Tooltip>
    );
  }

  return cardContent;
};
